import type { Config } from './grab'
import type { DateDay, DateTimeMs, Festival, GrabEvent } from '../grab/types'

export const nowMs = (): DateTimeMs => Date.now() as DateTimeMs

// Dim. 24 avr.
export const formatDayShort = (day: DateTimeMs | DateDay, config: Pick<Config, 'timezone' | 'lang'>): string =>
  new Date(day).toLocaleDateString(config.lang, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: config.timezone,
  })

// Vendredi 23 avril
export const formatDayLong = (day: DateTimeMs | DateDay, config: Pick<Config, 'timezone' | 'lang'>): string =>
  new Date(day).toLocaleDateString(config.lang, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: config.timezone,
  })

// Avril 23
export const formatMonthShort = (day: DateTimeMs | DateDay, config: Pick<Config, 'timezone' | 'lang'>): string => {
  const month = new Date(day).toLocaleDateString(config.lang, {
    month: 'long',
    year: '2-digit',
    timeZone: config.timezone,
  })
  return `${month.slice(0, 1).toUpperCase()}${month.slice(1)}`
}

export const showHour = (
  date: DateTimeMs | undefined,
  festival: Partial<Pick<Festival, 'alwaysShowEventHour' | 'showEventHourDelay'>>,
  current: DateTimeMs
): boolean => {
  if (!date) {
    return false
  }
  if (!festival.alwaysShowEventHour) {
    const limit = date - (festival.showEventHourDelay ?? 0) * 60 * 1000
    if (current < limit) {
      return false
    }
  }
  return true
}

// 19:00
export const formatHour = (date: DateTimeMs | DateDay, config: Pick<Config, 'lang' | 'timezone'>): string => {
  return new Date(date).toLocaleTimeString(config.lang, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: config.timezone,
  })
}

// "12:00 > 13:00" or "12:00" if endDate must not be shown
export const formatStartAndEndHour = (
  startDate: DateTimeMs | DateDay | undefined,
  endDate: DateTimeMs | DateDay | undefined,
  config: Pick<Config, 'lang' | 'timezone'>,
  showEndDate: boolean = true
): string => {
  const startTime = startDate ? formatHour(startDate, config) : ''
  const endTime = endDate && showEndDate ? ` > ${formatHour(endDate, config)}` : ''
  return `${startTime}${endTime}`
}

// "12:00 > 13:00" or "12:00" if endDate must not be shown
export const formatEndAndStartHour = (
  event: Pick<GrabEvent, 'showStartDate' | 'showEndDate' | 'hideEndDate'>,
  config: Pick<Config, 'lang' | 'timezone'>,
  festival: Partial<Pick<Festival, 'eventsHideEndDate'>>
): string => {
  return formatStartAndEndHour(event.showStartDate, event.showEndDate, config, !event.hideEndDate && !festival.eventsHideEndDate)
}

// Intl formatting is expensive on Hermes (toLocaleDateString rebuilds a formatter on every call)
// and getDateDay runs once per event when grouping the program of large festivals: reuse a single
// formatter per timezone and memoize the result per timestamp.
const dateDayFormatters = new Map<string | undefined, Intl.DateTimeFormat>()
const dateDayCache = new Map<string, DateDay>()

// 2022-12-30
export const getDateDay = (date: DateTimeMs, config: Pick<Config, 'timezone'>): DateDay => {
  const cacheKey = `${config.timezone}:${date}`
  const cached = dateDayCache.get(cacheKey)
  if (cached) {
    return cached
  }
  let formatter = dateDayFormatters.get(config.timezone)
  if (!formatter) {
    // Use the Sweden locale because it uses the ISO format
    formatter = new Intl.DateTimeFormat('sv', { timeZone: config.timezone })
    dateDayFormatters.set(config.timezone, formatter)
  }
  if (dateDayCache.size > 5000) {
    dateDayCache.clear()
  }
  // date - 6h
  const result = formatter.format(new Date(date - 21600000)) as DateDay
  dateDayCache.set(cacheKey, result)
  return result
}

export const getMonthDay = (date: DateTimeMs): DateDay => {
  // date - 6h
  const d = new Date(date - 21600000)
  // Use the Sweden locale because it uses the ISO format
  return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-01` as DateDay
}

export const addMinutes = (date: DateTimeMs, minutes: number): DateTimeMs => {
  return (date + minutes * 60 * 1000) as DateTimeMs
}

export const addSeconds = (date: DateTimeMs, seconds: number): DateTimeMs => {
  return (date + seconds * 1000) as DateTimeMs
}

export const startOfDay = (date: DateTimeMs): DateTimeMs => {
  const startOfDay = new Date(date)
  startOfDay.setUTCHours(0, 0, 0, 0)
  return startOfDay.getTime() as DateTimeMs
}
